
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { getMasterDatabaseRecordById } from '@/modules/adminPortal/api/masterDatabase.api';
import { Button } from '@/shared/components/ui/button';
import type { MasterDatabaseRecord } from '@/modules/adminPortal/api/masterDatabase.api';

import useCandidateDetails from '@/modules/hrPortal/hooks/useCandidateDetails';
import useCandidateDetailsActions from '@/modules/hrPortal/hooks/useCandidateDetailsActions';
import useInterviews from '@/modules/hrPortal/hooks/useInterviews';

import { getMyFeaturePermissions } from '@/modules/team/api/teamMemberFeaturePermissionApi';
import type { TeamMemberFeaturePermission } from '@/modules/team/api/teamMemberFeaturePermissionApi';

import { uploadInterviewAttachments } from '@/modules/hrPortal/api/interview.api';
import { getInterviewers } from '@/modules/team/api/team.api';

import { mapCandidateDetails } from '@/modules/hrPortal/utils/candidateDetailsMapper';
import type { CandidateDetailsResponse } from '@/modules/hrPortal/types';
import type { Interview } from '@/modules/hrPortal/types/interview.types';
import type { RootState } from '@/store/store';

import CandidateDetailsHeader from '@/modules/hrPortal/components/CandidateDetails/CandidateDetailsHeader';
import CandidateSummaryCard from '@/modules/hrPortal/components/CandidateDetails/CandidateSummaryCard';
import AIAnalysisCard from '@/modules/hrPortal/components/CandidateDetails/AIAnalysisCard';
import CandidateAnalysisTabs from '@/modules/hrPortal/components/CandidateDetails/CandidateAnalysisTabs';
import CandidateSidebar from '@/modules/hrPortal/components/CandidateDetails/CandidateSidebar';

import {
  BlacklistCandidateDialog,
  RejectCandidateDialog,
  ScheduleInterviewDialog,
} from '@/modules/hrPortal/components/CandidateDetails/CandidateDialogs';

function PortalApplicationCandidateDetailsPage() {
  const params = useParams();
  const navigate = useNavigate();

  // --------------------------------------------------
  // Candidate Status Dialog State
  // --------------------------------------------------

  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showBlacklistDialog, setShowBlacklistDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [blacklistReason, setBlacklistReason] = useState('');
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  // --------------------------------------------------
  // Schedule / Reschedule State
  // --------------------------------------------------

  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleRound, setScheduleRound] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleDuration, setScheduleDuration] = useState('60');
  const [scheduleMode, setScheduleMode] = useState('ONLINE');
  const [scheduleInterviewer, setScheduleInterviewer] = useState('');
  const [scheduleMeetingPlatform, setScheduleMeetingPlatform] = useState('');
  const [scheduleMeetingLink, setScheduleMeetingLink] = useState('');
  const [scheduleVenue, setScheduleVenue] = useState('');
  const [scheduleAddress, setScheduleAddress] = useState('');
  const [scheduleGoogleMapsLocation, setScheduleGoogleMapsLocation] = useState('');
  const [scheduleInstructions, setScheduleInstructions] = useState('');
  const [scheduleAttachments, setScheduleAttachments] = useState<File[]>([]);
  const [reschedulingInterviewId, setReschedulingInterviewId] = useState<string | null>(null);

  // Mandatory reason for rescheduling
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [activeInterviewers, setActiveInterviewers] = useState<any[]>([]);

  // --------------------------------------------------
  // Application ID
  // --------------------------------------------------

  const applicationId = String(
    (params as { applicationId?: string; id?: string }).applicationId ??
    (params as { applicationId?: string; id?: string }).id ??
    ''
  );

  // --------------------------------------------------
  // Candidate Details
  // --------------------------------------------------

  const {
    data,
    loading,
    refreshing,
    error,
    refetch: refetchCandidateDetails,
  } = useCandidateDetails(applicationId) as {
    data: CandidateDetailsResponse | null;
    loading: boolean;
    refreshing: boolean;
    error: unknown;
    refetch: () => Promise<any>;
  };

  const {
    resume,
    video,
    application,
    candidateData,
    aiAnalysis,
    allocation,
  } = mapCandidateDetails(data);

  const aiRun = data?.aiRun ?? null;
  const canGenerateAIReport = data?.canGenerateAIReport ?? false;

  // Candidate Allocation
  const isAllocatedToAnotherTeam =
    allocation?.isAllocatedToAnotherTeam ?? false;

  const isGloballyBlacklisted =
    allocation?.isBlacklisted ?? false;

  const normalizedAllocationStatus = String(
    allocation?.allocationStatus ??
    allocation?.status ??
    ''
  ).toUpperCase();

  const hasActiveAllocation = Boolean(
    allocation?.allocationTeamId
  );

  const allocationApplicationId =
    allocation?.allocationApplicationId ?? null;

  const isAllocatedToCurrentApplication =
    allocation?.isAllocatedToCurrentApplication ??
    (
      hasActiveAllocation &&
      Boolean(allocationApplicationId) &&
      String(allocationApplicationId) === String(applicationId)
    );

  // If the backend provides the permission, use it.
  // Otherwise, fail closed for legacy allocations without an application ID.
  const canManageCurrentApplication =
    allocation?.canManageApplication ??
    (
      !hasActiveAllocation ||
      isAllocatedToCurrentApplication
    );

  const isAllocatedToDifferentApplication =
    hasActiveAllocation &&
    !isAllocatedToAnotherTeam &&
    !isAllocatedToCurrentApplication;

  const canManageCandidateProcess =
    !isAllocatedToAnotherTeam &&
    !isGloballyBlacklisted &&
    canManageCurrentApplication;

  const allocationRestrictionMessage = isGloballyBlacklisted
    ? 'This candidate is globally blacklisted. Hiring-process actions are disabled until the blacklist is removed or expires.'
    : isAllocatedToAnotherTeam
      ? 'This candidate is allocated to another HR team. You can view the candidate, but cannot manage this hiring process.'
      : isAllocatedToDifferentApplication
        ? allocationApplicationId
          ? 'This candidate is allocated to another application. You can view the candidate, but cannot manage this application.'
          : 'This candidate has a legacy allocation without an application link. Hiring-process actions are disabled until the allocation is resolved.'
        : null;

  // --------------------------------------------------
  // Candidate Actions
  // --------------------------------------------------

  const {
    statusUpdating,
    updateCandidateStatus,
    saveNotes,
    downloadDocument,
    aiGenerating,
    generateAIReport,
  } = useCandidateDetailsActions(applicationId, {
    onMessage: setActionMessage,
  });

  // --------------------------------------------------
  // Interviews
  // --------------------------------------------------

  const {
    interviews,
    loading: interviewsLoading,
    scheduling,
    createInterview,
    updateInterviewSchedule,
    beginInterview,
    cancelScheduledInterview,
    markInterviewNoShow,
    finishInterview,
    refreshInterviews,
  } = useInterviews(applicationId);

  // --------------------------------------------------
  // Team Members / Feature Permissions
  // --------------------------------------------------

  const user = useSelector((state: RootState) => state.auth.user);
  const normalizedRole = user?.role?.toLowerCase() ?? '';

  const usesFeaturePermissions = [
    'recruiter',
    'operations',
    'manager',
  ].includes(normalizedRole);

  const [allowedFeatureKeys, setAllowedFeatureKeys] = useState<Set<string>>(new Set());
  const [featurePermissionsLoaded, setFeaturePermissionsLoaded] = useState(false);

  useEffect(() => {
    if (!usesFeaturePermissions || !user?.id) {
      setAllowedFeatureKeys(new Set());
      setFeaturePermissionsLoaded(true);
      return;
    }

    let cancelled = false;

    setFeaturePermissionsLoaded(false);
    setAllowedFeatureKeys(new Set());

    const loadPermissions = async () => {
      try {
        const permissions = await getMyFeaturePermissions();

        if (cancelled) return;

        setAllowedFeatureKeys(
          new Set(
            permissions
              .filter(
                (permission: TeamMemberFeaturePermission) =>
                  permission.permissionLevel === 'ALLOWED'
              )
              .map((permission) => permission.featureKey)
          )
        );
      } catch (permissionError) {
        console.error(
          'Failed to load candidate-page permissions:',
          permissionError
        );

        if (!cancelled) setAllowedFeatureKeys(new Set());
      } finally {
        if (!cancelled) setFeaturePermissionsLoaded(true);
      }
    };

    void loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [usesFeaturePermissions, user?.id]);

  const hasModulePermission = (featureKey: string) => {
    if (!usesFeaturePermissions) return true;

    return featurePermissionsLoaded && allowedFeatureKeys.has(featureKey);
  };


  const canAccessHiringDecisions = hasModulePermission('HIRING_DECISIONS') && canManageCandidateProcess;
  const canAccessGlobalBlacklist = hasModulePermission('GLOBAL_BLACKLIST') && canManageCandidateProcess;
  const canAccessInterviewScheduling = hasModulePermission('INTERVIEW_SCHEDULING') && canManageCandidateProcess;



  const completedRounds = useMemo(
    () =>
      interviews
        .filter((interview) => interview.status === 'COMPLETED')
        .map((interview) => interview.round),
    [interviews]
  );

  // --------------------------------------------------
  // Current Application Status
  // --------------------------------------------------

  const currentStatus =
    candidateData.statusHistory.length > 0
      ? candidateData.statusHistory[
        candidateData.statusHistory.length - 1
      ]?.status || 'Applied'
      : 'Applied';

  const [scheduleInterviewEnabled, setScheduleInterviewEnabled] = useState(false);

  useEffect(() => {
    const shortlisted = candidateData.statusHistory.some(
      (item) => item.status?.trim().toLowerCase() === 'shortlisted'
    );

    if (shortlisted) {
      setScheduleInterviewEnabled(true);
    }
  }, [candidateData.statusHistory]);

  const canScheduleInterview =
    scheduleInterviewEnabled && canAccessInterviewScheduling;

  // --------------------------------------------------
  // Job Position
  // --------------------------------------------------

  const jobPosition =
    (application as any)?.job?.title ||
    (application as any)?.job?.position ||
    (application as any)?.jobTitle ||
    (candidateData as any)?.position ||
    '';

  // --------------------------------------------------
  // Interview Workflow
  // --------------------------------------------------

  const interviewWorkflow = useMemo(
    () =>
      Array.isArray((application as any)?.interviewWorkflow)
        ? [...(application as any).interviewWorkflow].sort(
          (a, b) => a.sequence - b.sequence
        )
        : [],
    [application]
  );

  // --------------------------------------------------
  // Application Selection
  // --------------------------------------------------

  const handleApplicationSelect = (selectedApplicationId: string) => {
    if (!selectedApplicationId || selectedApplicationId === applicationId) {
      return;
    }

    const currentPath = window.location.pathname;
    const segments = currentPath.split('/');
    segments[segments.length - 1] = selectedApplicationId;

    navigate(segments.join('/'));
  };

  // --------------------------------------------------
  // Fetch Team Members
  // --------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    const loadInterviewers = async () => {
      if (!canAccessInterviewScheduling) {
        setActiveInterviewers([]);
        return;
      }

      try {
        const response = await getInterviewers();

        const members = Array.isArray(response)
          ? response
          : response?.data ?? [];

        if (cancelled) return;

        setActiveInterviewers(
          members.filter(
            (member: any) => member.status?.toLowerCase() === 'active'
          )
        );
      } catch (interviewerError) {
        console.error('Failed to load interviewers:', interviewerError);

        if (!cancelled) {
          setActiveInterviewers([]);
        }
      }
    };

    void loadInterviewers();

    return () => {
      cancelled = true;
    };
  }, [canAccessInterviewScheduling]);

  // --------------------------------------------------
  // Load HR Notes
  // --------------------------------------------------

  useEffect(() => {
    if (!application) return;

    setNotes(application.hrNotes ?? '');
  }, [application]);

  // --------------------------------------------------
  // Clear Action Message
  // --------------------------------------------------

  useEffect(() => {
    if (!actionMessage) return;

    const timer = window.setTimeout(() => {
      setActionMessage(null);
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [actionMessage]);

  useEffect(() => {
    if (!showScheduleDialog || reschedulingInterviewId) {
      return;
    }

    // Find the next eligible interview round.
    const sortedWorkflow = [...interviewWorkflow].sort(
      (a, b) => a.sequence - b.sequence
    );

    const nextRound = sortedWorkflow.find(
      (round) => !completedRounds.includes(round.key)
    );

    setScheduleRound(nextRound?.key || '');

    // Reset schedule fields.
    setScheduleDate('');
    setScheduleTime('');
    setScheduleDuration('60');
    setScheduleMode('ONLINE');
    setScheduleInterviewer('');
    setScheduleMeetingPlatform('');
    setScheduleMeetingLink('');
    setScheduleVenue('');
    setScheduleAddress('');
    setScheduleGoogleMapsLocation('');
    setScheduleInstructions('');
    setScheduleAttachments([]);
    setRescheduleReason('');
  }, [
    showScheduleDialog,
    reschedulingInterviewId,
    interviewWorkflow,
    completedRounds,
  ]);

  // --------------------------------------------------
  // Open New Schedule
  // --------------------------------------------------

  const handleScheduleInterview = () => {
    if (!canAccessInterviewScheduling) return;

    if (!applicationId) {
      setActionMessage('Candidate application ID is not available.');
      return;
    }

    setReschedulingInterviewId(null);
    setRescheduleReason('');
    setActionMessage(null);
    setShowScheduleDialog(true);
  };

  // --------------------------------------------------
  // Open Reschedule
  // --------------------------------------------------

  const handleOpenReschedule = (interview: Interview) => {
    if (!canAccessInterviewScheduling) return;

    setReschedulingInterviewId(interview.id);
    setScheduleRound(interview.round);
    setScheduleDate(interview.date ? interview.date.split('T')[0] : '');
    setScheduleTime(interview.time || '');
    setScheduleDuration(String(interview.duration || 60));
    setScheduleMode(interview.mode || 'ONLINE');
    setScheduleInterviewer(interview.interviewerId || '');
    setScheduleMeetingPlatform(interview.meetingPlatform || '');
    setScheduleMeetingLink(interview.meetingLink || '');
    setScheduleVenue(interview.venue || '');
    setScheduleAddress(interview.address || '');
    setScheduleGoogleMapsLocation(interview.googleMapsLocation || '');
    setScheduleInstructions(interview.instructions || '');
    setScheduleAttachments([]);
    setRescheduleReason('');
    setActionMessage(null);
    setShowScheduleDialog(true);
  };

  // --------------------------------------------------
  // Save Schedule / Reschedule
  // --------------------------------------------------

  const handleSaveInterviewSchedule = async () => {
    if (!canAccessInterviewScheduling) {
      setShowScheduleDialog(false);
      return;
    }

    if (!scheduleRound) {
      setActionMessage('Interview round is required.');
      return;
    }

    if (!scheduleDate) {
      setActionMessage('Interview date is required.');
      return;
    }

    if (!scheduleTime) {
      setActionMessage('Interview time is required.');
      return;
    }

    if (!scheduleDuration) {
      setActionMessage('Interview duration is required.');
      return;
    }

    if (!scheduleInterviewer) {
      setActionMessage('Interviewer is required.');
      return;
    }

    if (!scheduleMode) {
      setActionMessage('Interview mode is required.');
      return;
    }

    // Online validation.
    if (
      scheduleMode === 'ONLINE' &&
      (!scheduleMeetingPlatform || !scheduleMeetingLink.trim())
    ) {
      setActionMessage(
        'Meeting platform and meeting link are required for online interviews.'
      );
      return;
    }

    // Offline validation.
    if (
      scheduleMode === 'OFFLINE' &&
      (!scheduleVenue.trim() ||
        !scheduleAddress.trim() ||
        !scheduleGoogleMapsLocation.trim())
    ) {
      setActionMessage(
        'Venue, complete address, and Google Maps location are required for offline interviews.'
      );
      return;
    }

    // Reschedule reason validation.
    if (reschedulingInterviewId && !rescheduleReason.trim()) {
      setActionMessage('Reschedule reason is required.');
      return;
    }

    try {
      setActionMessage(null);

      // Reschedule.
      if (reschedulingInterviewId) {
        await updateInterviewSchedule(reschedulingInterviewId, {
          date: scheduleDate,
          time: scheduleTime,
          duration: Number(scheduleDuration),
          interviewerId: scheduleInterviewer,
          mode: scheduleMode as 'ONLINE' | 'OFFLINE' | 'PHONE',
          ...(scheduleMode === 'ONLINE'
            ? {
              meetingPlatform: scheduleMeetingPlatform.trim(),
              meetingLink: scheduleMeetingLink.trim(),
            }
            : {}),
          ...(scheduleMode === 'OFFLINE'
            ? {
              venue: scheduleVenue.trim(),
              address: scheduleAddress.trim(),
              googleMapsLocation: scheduleGoogleMapsLocation.trim(),
            }
            : {}),
          instructions: scheduleInstructions.trim() || undefined,
          reason: rescheduleReason.trim(),
        });

        // Reschedule attachments.
        if (scheduleAttachments.length > 0) {
          await uploadInterviewAttachments(
            reschedulingInterviewId,
            scheduleAttachments
          );
        }

        toast.success('Interview rescheduled successfully.');
        await refreshInterviews();
        await refetchCandidateDetails();

        setReschedulingInterviewId(null);
        setRescheduleReason('');
        setScheduleAttachments([]);
        setShowScheduleDialog(false);
        return;
      }

      // New interview schedule.
      const createdInterview = await createInterview({
        applicationId,
        candidateUserId:
          (application as any)?.candidateUserId ||
          (candidateData as any)?.candidateUserId ||
          undefined,
        jobId:
          (application as any)?.jobId ||
          (candidateData as any)?.jobId ||
          undefined,
        round: scheduleRound,
        date: scheduleDate,
        time: scheduleTime,
        duration: Number(scheduleDuration),
        interviewerId: scheduleInterviewer,
        mode: scheduleMode as 'ONLINE' | 'OFFLINE' | 'PHONE',
        ...(scheduleMode === 'ONLINE'
          ? {
            meetingPlatform: scheduleMeetingPlatform.trim(),
            meetingLink: scheduleMeetingLink.trim(),
          }
          : {}),
        ...(scheduleMode === 'OFFLINE'
          ? {
            venue: scheduleVenue.trim(),
            address: scheduleAddress.trim(),
            googleMapsLocation: scheduleGoogleMapsLocation.trim(),
          }
          : {}),
        instructions: scheduleInstructions.trim() || undefined,
      });

      // New interview attachments.
      if (scheduleAttachments.length > 0) {
        if (!createdInterview?.id) {
          throw new Error(
            'Interview was created, but interview ID was not returned.'
          );
        }

        await uploadInterviewAttachments(
          createdInterview.id,
          scheduleAttachments
        );
      }

      toast.success('Interview scheduled successfully.');
      await refreshInterviews();

      // Refresh application status immediately.
      await refetchCandidateDetails();

      setScheduleAttachments([]);
      setShowScheduleDialog(false);
    } catch (error: any) {
      toast.error(
        typeof error === 'string'
          ? error
          : error?.message ||
          (reschedulingInterviewId
            ? 'Unable to reschedule interview.'
            : 'Unable to schedule interview.')
      );
    }
  };

  // --------------------------------------------------
  // Start Interview
  // --------------------------------------------------

  const handleStartInterview = async (interviewId: string) => {
    if (!canAccessInterviewScheduling) {
      throw new Error('You do not have permission to manage interviews.');
    }

    try {
      await beginInterview(interviewId);
      toast.success('Interview started successfully.');
      await refreshInterviews();
    } catch (error: any) {
      throw new Error(
        typeof error === 'string'
          ? error
          : error?.message || 'Unable to start interview.'
      );
    }
  };

  // --------------------------------------------------
  // Cancel Interview
  // --------------------------------------------------

  const handleCancelInterview = async (
    interviewId: string,
    reason: string
  ) => {
    if (!canAccessInterviewScheduling) {
      throw new Error('You do not have permission to manage interviews.');
    }

    try {
      await cancelScheduledInterview(interviewId, reason);
      toast.success('Interview cancelled successfully.');
      await refreshInterviews();
    } catch (error: any) {
      throw new Error(
        typeof error === 'string'
          ? error
          : error?.message || 'Unable to cancel interview.'
      );
    }
  };

  // --------------------------------------------------
  // No Show
  // --------------------------------------------------

  const handleNoShowInterview = async (
    interviewId: string,
    reason: string
  ) => {
    if (!canAccessInterviewScheduling) {
      throw new Error('You do not have permission to manage interviews.');
    }

    try {
      await markInterviewNoShow(interviewId, reason);
      toast.success('Interview marked as no-show successfully.');
      await refreshInterviews();
    } catch (error: any) {
      throw new Error(
        typeof error === 'string'
          ? error
          : error?.message || 'Unable to mark interview as no-show.'
      );
    }
  };

  // --------------------------------------------------
  // Complete Interview
  // --------------------------------------------------

  const handleCompleteInterview = async (
    interviewId: string,
    result: 'PASSED' | 'FAILED',
    remarks?: string
  ) => {
    if (!canAccessInterviewScheduling) {
      throw new Error('You do not have permission to manage interviews.');
    }

    try {
      await finishInterview(interviewId, result, remarks);

      if (result === 'PASSED') {
        toast.success('Interview completed successfully.');
      } else {
        toast.error('Interview completed and candidate rejected.');
      }

      await refreshInterviews();

      // Keep application status synchronized immediately.
      await refetchCandidateDetails();
    } catch (error: any) {
      throw new Error(
        typeof error === 'string'
          ? error
          : error?.message || 'Unable to complete interview.'
      );
    }
  };

  // --------------------------------------------------
  // Save Notes
  // --------------------------------------------------

  const handleSaveNotes = async () => {
    if (!canManageCandidateProcess) {
      setActionMessage(
        allocationRestrictionMessage ||
        'You cannot manage this candidate process.'
      );
      return;
    }

    await saveNotes(notes);
  };

  useEffect(() => {
    if (!canAccessInterviewScheduling) {
      setShowScheduleDialog(false);
      setReschedulingInterviewId(null);
    }

    if (!canAccessHiringDecisions) {
      setShowRejectDialog(false);
    }

    if (!canAccessGlobalBlacklist) {
      setShowBlacklistDialog(false);
    }
  }, [
    canAccessInterviewScheduling,
    canAccessHiringDecisions,
    canAccessGlobalBlacklist,
  ]);

  // --------------------------------------------------
  // Loading
  // --------------------------------------------------

  if (loading && !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50/50">
        <p className="text-sm text-muted-foreground">
          Loading candidate details...
        </p>
      </div>
    );
  }

  // --------------------------------------------------
  // Error
  // --------------------------------------------------

  if (error && !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50/50">
        <p className="text-sm text-red-600">
          Unable to load candidate details.
        </p>
      </div>
    );
  }

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <div className="min-h-screen w-full min-w-0 overflow-x-clip bg-slate-50/50">
      <CandidateDetailsHeader
        candidateData={candidateData}
        aiAnalysis={aiAnalysis}
        currentStatus={currentStatus}
        onBack={() => navigate(-1)}
        onScheduleInterview={handleScheduleInterview}
        canScheduleInterview={canScheduleInterview}
        canAccessInterviewScheduling={canAccessInterviewScheduling}
        onMessage={(message) => setActionMessage(message)}
      />

      {/* Action Message */}
      {actionMessage && (
        <div className="mx-auto w-full max-w-[1600px] px-3 pt-4 sm:px-5 lg:px-8">
          <div className="break-words rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 text-sm text-blue-700 sm:px-4">
            {actionMessage}
          </div>
        </div>
      )}

      {/* Candidate Allocation Status */}
      {allocation && (
        <div className="mx-auto w-full max-w-[1600px] px-3 pt-4 sm:px-5 lg:px-8">
          <div
            className={`break-words rounded-lg border px-3 py-3 text-sm sm:px-4 ${isGloballyBlacklisted
                ? 'border-red-200 bg-red-50 text-red-800'
                : isAllocatedToAnotherTeam
                  ? 'border-amber-200 bg-amber-50 text-amber-900'
                  : normalizedAllocationStatus === 'ALLOCATED'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : 'border-slate-200 bg-white text-slate-700'
              }`}
            role="status"
          >
            <div className="font-semibold">
              {isGloballyBlacklisted
                ? 'Globally blacklisted'
                : isAllocatedToAnotherTeam
                  ? 'Allocated to another HR team'
                  : normalizedAllocationStatus === 'ALLOCATED'
                    ? 'Candidate allocated to your HR team'
                    : 'Candidate available for allocation'}
            </div>

            {allocationRestrictionMessage && (
              <p className="mt-1">{allocationRestrictionMessage}</p>
            )}

            {!allocationRestrictionMessage && allocation.allocationStartedAt && (
              <p className="mt-1 text-xs opacity-80">
                Allocation started:{' '}
                {new Date(allocation.allocationStartedAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mx-auto w-full max-w-[1600px] min-w-0 px-3 py-4 sm:px-5 sm:py-6 lg:px-8">
        <div className="grid min-w-0 grid-cols-1 items-start gap-4 sm:gap-5 lg:grid-cols-3 lg:gap-6">
          {/* Main Content */}
          <div className="min-w-0 space-y-4 sm:space-y-5 lg:col-span-2 lg:space-y-6">
            <CandidateSummaryCard
              candidateData={candidateData}
              currentStatus={currentStatus}
              statusUpdating={statusUpdating}
              canAccessHiringDecisions={canAccessHiringDecisions}
              canAccessGlobalBlacklist={canAccessGlobalBlacklist}
              onNextStatus={async (status) => {
                if (!canAccessHiringDecisions) return;

                await updateCandidateStatus(status);

                if (status.trim().toLowerCase() === 'shortlisted') {
                  setScheduleInterviewEnabled(true);
                }
              }}
              onReject={() => {
                if (canAccessHiringDecisions) setShowRejectDialog(true);
              }}
              onBlacklist={() => {
                if (canAccessGlobalBlacklist) setShowBlacklistDialog(true);
              }}
              applications={data?.applications ?? []}
              currentApplicationId={applicationId}
              onApplicationSelect={handleApplicationSelect}
            />

            <AIAnalysisCard
              aiAnalysis={aiAnalysis}
              aiRun={aiRun}
              onGenerateAIReport={generateAIReport}
              aiGenerating={aiGenerating}
              canGenerateAIReport={canGenerateAIReport}
            />

            <CandidateAnalysisTabs
              aiAnalysis={aiAnalysis}
              video={video}
              applicationId={applicationId}
              statusUpdating={statusUpdating}
              onMessage={setActionMessage}
            />
          </div>

          {/* Sidebar */}
          <aside className="min-w-0 w-full">
            <CandidateSidebar
              candidateData={candidateData}
              resume={resume}
              video={video}
              applicationId={applicationId}
              notes={notes}
              onNotesChange={setNotes}
              onSaveNotes={handleSaveNotes}
              onDownload={downloadDocument}
              interviews={interviews}
              interviewsLoading={interviewsLoading}
              interviewWorkflow={interviewWorkflow}
              onStartInterview={handleStartInterview}
              onCompleteInterview={handleCompleteInterview}
              onCancelInterview={handleCancelInterview}
              onOpenReschedule={handleOpenReschedule}
              canAccessInterviewScheduling={canAccessInterviewScheduling}
            />
          </aside>
        </div>
      </div>

      {/* Schedule / Reschedule Dialog */}
      <ScheduleInterviewDialog
        open={showScheduleDialog && canAccessInterviewScheduling}
        onOpenChange={(open) => {
          setShowScheduleDialog(open);

          if (!open) {
            setReschedulingInterviewId(null);
            setRescheduleReason('');
            setScheduleAttachments([]);
          }
        }}
        isReschedule={Boolean(reschedulingInterviewId)}
        candidateName={candidateData.name}
        jobPosition={jobPosition}
        scheduleRound={scheduleRound}
        scheduleDate={scheduleDate}
        scheduleTime={scheduleTime}
        scheduleDuration={scheduleDuration}
        scheduleMode={scheduleMode}
        scheduleInterviewer={scheduleInterviewer}
        scheduleMeetingPlatform={scheduleMeetingPlatform}
        scheduleMeetingLink={scheduleMeetingLink}
        scheduleVenue={scheduleVenue}
        scheduleAddress={scheduleAddress}
        scheduleGoogleMapsLocation={scheduleGoogleMapsLocation}
        scheduleInstructions={scheduleInstructions}
        attachments={scheduleAttachments}
        interviewers={activeInterviewers}
        completedRounds={completedRounds}
        interviewWorkflow={interviewWorkflow}
        rescheduleReason={rescheduleReason}
        onRescheduleReasonChange={setRescheduleReason}
        scheduleUpdating={scheduling}
        onRoundChange={setScheduleRound}
        onDateChange={setScheduleDate}
        onTimeChange={setScheduleTime}
        onDurationChange={setScheduleDuration}
        onModeChange={setScheduleMode}
        onInterviewerChange={setScheduleInterviewer}
        onMeetingPlatformChange={setScheduleMeetingPlatform}
        onMeetingLinkChange={setScheduleMeetingLink}
        onVenueChange={setScheduleVenue}
        onAddressChange={setScheduleAddress}
        onGoogleMapsLocationChange={setScheduleGoogleMapsLocation}
        onInstructionsChange={setScheduleInstructions}
        onAttachmentsChange={setScheduleAttachments}
        onSave={handleSaveInterviewSchedule}
      />

      {/* Reject Dialog */}
      <RejectCandidateDialog
        open={showRejectDialog && canAccessHiringDecisions}
        onOpenChange={setShowRejectDialog}
        reason={rejectReason}
        statusUpdating={statusUpdating}
        onReasonChange={setRejectReason}
        onConfirm={async () => {
          if (!canAccessHiringDecisions) return;

          const success = await updateCandidateStatus('Rejected', rejectReason);

          if (success) {
            setShowRejectDialog(false);
            setRejectReason('');
          }
        }}
      />

      {/* Blacklist Dialog */}
      <BlacklistCandidateDialog
        open={showBlacklistDialog && canAccessGlobalBlacklist}
        onOpenChange={setShowBlacklistDialog}
        reason={blacklistReason}
        statusUpdating={statusUpdating}
        onReasonChange={setBlacklistReason}
        onConfirm={async () => {
          if (!canAccessGlobalBlacklist) return;

          const success = await updateCandidateStatus(
            'Blacklisted',
            blacklistReason.trim()
          );

          if (success) {
            setShowBlacklistDialog(false);
            setBlacklistReason('');
          }
        }}
      />
    </div>
  );
}

/**
 * Imported/manual Master Database records use the same Candidate Details
 * components and layout as portal applications. Only the data source differs.
 * No application, interview, notes-write, or hiring-decision API is called.
 */

function ImportedMasterDatabaseCandidateDetailsPage({ recordId }: { recordId: string }) {
  const navigate = useNavigate();
  const [record, setRecord] = useState<MasterDatabaseRecord | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(true);
  const [recordError, setRecordError] = useState('');

  const masterDatabaseInterviewData = {
  interviewLineup: record?.interviewLineup ?? '',
  interviewLinedupDate: record?.interviewLinedupDate ?? '',
  hrInterviewDate: record?.hrInterviewDate ?? '',
  hrInterviewName: record?.hrInterviewName ?? '',
  hrInterviewStatus: record?.hrInterviewStatus ?? '',
  business1Date: record?.business1Date ?? '',
  business1InterviewerName: record?.business1InterviewerName ?? '',
};

  useEffect(() => {
    let cancelled = false;

    const loadRecord = async () => {
      setLoadingRecord(true);
      setRecordError('');

      try {
        const response = await getMasterDatabaseRecordById(recordId);
        const payload = (response as { data?: MasterDatabaseRecord } | null)?.data ?? response;

        if (!payload || typeof payload !== 'object') {
          throw new Error('Master Database record was not returned by the server.');
        }

        if (!cancelled) setRecord(payload as MasterDatabaseRecord);
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : 'Unable to load this Master Database record.';
        if (!cancelled) setRecordError(message);
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    };

    void loadRecord();
    return () => {
      cancelled = true;
    };
  }, [recordId]);

  const emptyView = mapCandidateDetails(null);

  const candidateData = {
    ...emptyView.candidateData,
    id: record?.id ?? '',
    name: record?.candidateName ?? '',
    email: '',
    phone: record?.contactNo ?? '',
    location: record?.location ?? '',
    position: record?.position ?? '',
    department: record?.entity ?? '',
    experience: record?.totalExperience ?? '',
    currentCompany: record?.currentCompany ?? '',
    currentSalary: record?.currentInhand ?? '',
    expectedSalary: record?.expectedInhand ?? '',
    noticePeriod: record?.noticePeriod ?? '',
    source: record?.source ?? '',
    statusHistory: [],
  } as typeof emptyView.candidateData;

  const resume = null as typeof emptyView.resume;
  const video = null as typeof emptyView.video;
  const aiAnalysis = emptyView.aiAnalysis;
  const currentStatus = 'Imported';
  const noAction = () => undefined;
  const noGenerateAIReport = async (): Promise<boolean> => false;

  if (loadingRecord) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50/50">
        <p className="text-sm text-muted-foreground">Loading candidate details...</p>
      </div>
    );
  }

  if (recordError || !record) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50/50">
        <div className="space-y-3 text-center">
          <p className="text-sm text-red-600">
            {recordError || 'Master Database record not found.'}
          </p>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Back to Master Database
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full min-w-0 overflow-x-clip bg-slate-50/50">
      <CandidateDetailsHeader
        candidateData={candidateData}
        aiAnalysis={aiAnalysis}
        currentStatus={currentStatus}
        onBack={() => navigate(-1)}
        onScheduleInterview={noAction}
        canScheduleInterview={false}
        canAccessInterviewScheduling={false}
        onMessage={noAction}
      />

      <div className="mx-auto w-full max-w-[1600px] min-w-0 px-3 py-4 sm:px-5 sm:py-6 lg:px-8">
        <div className="grid min-w-0 grid-cols-1 items-start gap-4 sm:gap-5 lg:grid-cols-3 lg:gap-6">
          <div className="min-w-0 space-y-4 sm:space-y-5 lg:col-span-2 lg:space-y-6">
            <CandidateSummaryCard
              candidateData={candidateData}
              currentStatus={currentStatus}
              // Keep the original decision controls visible, but disabled.
              statusUpdating={true}
              canAccessHiringDecisions={true}
              canAccessGlobalBlacklist={true}
              onNextStatus={async () => undefined}
              onReject={noAction}
              onBlacklist={noAction}
              applications={[]}
              currentApplicationId=""
            />

            <AIAnalysisCard
              aiAnalysis={aiAnalysis}
              aiRun={null}
              onGenerateAIReport={noGenerateAIReport}
              aiGenerating={false}
              canGenerateAIReport={false}
            />

            {/* Keep the existing analysis tabs/layout. No video API is called
                because this imported record has no portal application or video. */}
            <CandidateAnalysisTabs
              aiAnalysis={aiAnalysis}
              video={video}
              applicationId=""
              statusUpdating={false}
              onMessage={noAction}
            />
          </div>

          <aside className="min-w-0 w-full">
            <CandidateSidebar
              candidateData={candidateData}
              resume={resume}
              video={video}
              applicationId=""
              notes=""
              onNotesChange={noAction}
              onSaveNotes={noAction}
              onDownload={noAction}
              interviews={[]}
              interviewsLoading={false}
              interviewWorkflow={[]}
              canAccessInterviewScheduling={false}
              isMasterDatabaseRecord={true}
              masterDatabaseInterviewData={masterDatabaseInterviewData}
              />
          </aside>
        </div>
      </div>
    </div>
  );
}

export default function CandidateDetailsPage() {
  const params = useParams();

  if (params.recordId) {
    return <ImportedMasterDatabaseCandidateDetailsPage recordId={params.recordId} />;
  }

  return <PortalApplicationCandidateDetailsPage />;
}
